import { normalizeLang } from "../i18n.js";

import { pickGenderText } from "../utils/genderText.js";

const SYSTEM_STRINGS = {
  he: {
    nav: {
      about: "אודות",
      companion: "מלווה",
      journey: "מסע",
      problems: "בחירת נושא",
      account: "חשבון",
      home: "בית",
      voice: "שיחת קול",
    },
    progressBar: {
      title: "ההתקדמות שלי במסע",
      unit: "ימים הושלמו",
    },
    journeyDay: {
      dayCount: (day, total) => `יום ${day} מתוך ${total}`,
      backToJourney: "חזרה למסע",
      companion: "מלווה",
      liveMode: "קשה לי עכשיו",
      journeyPhaseStepTitle: "מה קורה בי עכשיו",
      aiCoachCta: "שיקוף קצר",
      aiCoachLoading: "מכין שיקוף קצר…",
      aiCoachTitle: "מה קורה בי עכשיו",
      aiCoachStep: "הצעד הקטן שלי",
      aiCoachQuestion: "השאלה שלי",
      aiCoachRetry: "השיקוף לא זמין כרגע",
      aiCoachError: "השיקוף לא זמין כרגע.",
      aiCoachSafety: "כרגע חשוב לי לפנות לעזרה מקצועית או לקו סיוע.",
      aiCoachSafetyCta: "אני פונה לעזרה עכשיו",
      uploadAudio: "אני מוסיף הקלטה",
      uploadingAudio: "אני מעלה…",
      ttsCloud: "אני שומע בקול ענן",
      play: "נגן",
      pause: "הפסק",
      listenToQuestionHint: "השמעת השאלה",
      writingQuestionHeading: "השאלה שלי",
      writingZoneLabel: "התשובה שלי",
      postWritingMirrorTitle: "כשאני חוזר על מה שכתבתי — זה מה שעולה בי",
      /** Week-one short mirror after three writing prompts — title only (body is unified below). */
      unifiedShortMirrorTitle: "זה מה שעולה מתוך המילים שלך",
      mirrorJournalWithQuestion: (question, answer) =>
        `עצרתי לרגע עם «${question}». זה מה שכתבתי לעצמי: ${answer}`,
      mirrorJournalNoQuestion: (answer) => `זה נוסף על מה שיצא לי בכתיבה: ${answer}`,
      continue: "המשך",
      back: "חזרה",
      skip: "דלג",
      record: "הקלט",
      stopRecord: "הפסק",
      resetDay: "להתחיל את היום מחדש",
      mindfulnessListeningSecondary: "אמשיך אחר כך",
      mindfulnessUpperMirrorLines: [
        "אני שם לב שלפעמים, גם כשאני עם אנשים, אני לא באמת נוכח.",
        "זה מעורר בי רצון להבין מה קורה לי ברגעים האלה.",
      ],
      resetConfirm: "לאפס את היום ולהתחיל מחדש? כל הכתיבה של היום תימחק.",
      resetCancel: "ביטול",
      resetConfirmCta: "איפוס והתחלה מחדש",
      questionLabel: (index) => `שאלה ${index}`,
      leadLine: "אין כאן נכון או לא נכון. רק אמת שקטה.",
      missingContent: "חסר תוכן למסך הזה.",
      placeholderDefaults: [
        "כתוב כאן בעדינות… מה עולה לך כשאתה שואל את עצמך את השאלה הזו?",
        "" ,
        "שים לב רגע לגוף. איפה זה יושב? לחץ, חום, כיווץ? כתוב את זה פשוט.",
        "סכם במשפט אחד: מה האמת הכי קטנה שאתה יכול להגיד עכשיו?",
      ],
    },
    journeyOverview: {
      pageTitle: "מסע הריפוי שלכם – מבט מלמעלה",
      intakeSummaryTitle: "מה שכתבתי בהתחלה",
      daysSummaryTitle: "איך התקדמת לאורך הימים",
      journalCta: "פתח את היומן המלא שלי",
      dayStatusNotStarted: "עוד לא התחלת",
      dayStatusInProgress: "בתהליך",
      dayStatusCompleted: "הושלם",
      openDay: "פתח את היום",
      resumeDay: "המשך כתיבה",
      completedDay: "צפה במה שכתבת",
      noIntakeYet: "עוד לא מילאת את טופס הפתיחה למסע הזה.",
      goToIntake: "עבור לטופס הפתיחה",
      dayLabel: "יום",
      dayLocked: "ייפתח אחרי שתשלים את היום הקודם",
      presenceLine: "אם תרצה לעצור רגע — אני כאן.",
      insightsTitle: "מבט קצר על מה שחוזר בי",
      insightsRepeating: "מה חוזר בי",
      insightsHelped: "מה עזר לי",
      insightsNext: "הצעד הבא שלי",
      moodTitle: "איך אני מרגיש/ה היום?",
      energyTitle: "כמה אנרגיה יש לי היום?",
      saveSignals: "לשמור",
      continueLastDay: "לחזור ליום האחרון",
      chooseProblem: "בחירת בעיה",
      summaryCta: "סיכום המסע",
      intakePainLabel: "מה הכי כואב עכשיו:",
      intakeGoalLabel: "לאן הייתי רוצה להגיע בסוף:",
      intakeWordLabel: "מילה אחת על היום:",
    },
    intake: {
      headerTitle: "נעצור רגע לפני שמתחילים",
      introLine1: (topic) => `לפני שמתחילים, אני עוצר/ת רגע מול הנושא: ${topic}.`,
      introLine2: "אני כותב/ת בכנות, צעד אחד בכל פעם.",
      cardPainTitle: "במשפט אחד: מה הכי כואב לי עכשיו?",
      cardPainDesc: "קצר. פשוט. אמיתי.",
      cardPainPlaceholder: "משפט אחד כאן.",
      cardGoalTitle: "מה הייתי רוצה שירגיש אחרת בסוף המסע?",
      cardGoalDesc: "לדוגמה: יותר רוגע, פחות בדיקות, יותר ביטחון, לומר צרכים",
      cardGoalPlaceholder: "משפט אחד כאן.",
      cardWordTitle: "מילה אחת שמתארת איך אני מרגיש/ה עכשיו",
      cardWordDesc: "פשוט. ישיר. מילה אחת.",
      cardWordPlaceholder: "מילה אחת",
      startJourney: "להמשיך למסע",
      micStart: "דבר/י, אתמלל",
      micStop: "עצור קול",
      micNoSupport: "הדפדפן לא תומך בדיבור",
      micNoSupportApp:
        "באפליקציה ב־iPhone/iPad תמלול דיבור לטקסט (של הדפדפן) בדרך כלל לא זמין. אפשר למלא את השדות במקלדת — או לפתוח את אותו מסך בספארי/כרום מהמכשיר.",
      micErrorBlocked: "אין גישה למיקרופון. באנדרואיד: הגדרות ← InnerVoice ← הרשאות ← מיקרופון. בדפדפן: אשר הקלטה כשהדפדפן שואל.",
      micErrorNetwork: "התמלול לא הצליח (רשת או שירות דיבור). נסה שוב או כתוב במקלדת.",
      micErrorGeneric: "התמלול נעצר. נסה שוב או המשך במקלדת.",
      chooseAnotherProblem: "לבחור בעיה אחרת",
      requiredError: "נא להשלים את השדות החובה.",
      summaryCta: "להתחיל בגרסה קצרה",
      summaryLoading: "מכין גרסה קצרה…",
      summaryTitle: "תקציר קצר בשבילי",
      summaryNeeds: "מה חשוב לי עכשיו",
      summaryStart: "משפט פתיחה בשבילי",
      summarySafety: "כרגע חשוב לי לפנות לעזרה מקצועית או לקו סיוע.",
      summarySafetyCta: "אני פונה לעזרה עכשיו",
    },
    helpNow: {
      title: "עזרה עכשיו",
      body: [
        "אם אתם מרגיש בסכנה מיידית, או שיש מחשבות לפגוע בעצמךבאחרים:",
        "פנה מיד לשירותי חירום במדינה שלכם (למשל 911/112/999 לפי אזור).",
        "אם אפשר—פנה לאדם קרוב ובקש להיות איתכם עכשיו.",
        "אם יש מוקד תמיכה רגשי מקומי—התקשר אליו עכשיו.",
      ],
      back: "חזור למסע",
      showLocal: "הצג מספרי חירום מקומיים",
      messageClose: "שלח הודעה לאדם קרוב",
    },
    journal: {
      title: "היומן המלא שלי במסע הזה",
      dayHeaderPrefix: "יום",
      backToOverview: "חזרה לסיכום המסע",
      noEntries: "עדיין לא נשמרו רשומות ביומן למסע הזה.",
      questionLabel: "שאלה",
      searchPlaceholder: "חיפוש בתוך היומן...",
      filterAll: "כל הימים",
      exportJson: "ייצוא JSON",
      exportTxt: "ייצוא TXT",
      emptyDay: "אין עדיין כתיבה ביום הזה.",
    },
    chat: {
      title: "ליווי מה־InnerVoice שלכם",
      welcome: "היי, אני InnerVoice שלכם. תכתוב לי בכנות מה מעסיק אותך היום, ואני אהיה איתכם צעד־צעד.",
      placeholder: "תכתוב כאן מה שהלב שלכם רוצה לספר ל־InnerVoice…",
      send: "שלח",
      back: "← לחזור ליום",
    },
    voiceChat: {
      title: "המלווה שלך",
      subtitle: "דבר/י חופשי. אני מקשיב, ואחר כך אגיב בעדינות ובאמת.",
      introHint: "אפשר להתחיל במשפט אחד. לא צריך יותר מזה עכשיו.",
      back: "← חזרה ליום במסע",
      typePlaceholder: "או לכתוב כאן במקום לדבר...",
      micStart: "התחל/י לדבר",
      micStop: "עצור הקלטה",
      send: "שלח",
      listening: "מאזין...",
      processing: "מעבד...",
      stop: "עצור השמעה",
      resume: "המשך השמעה",
      pause: "השמע תגובה",
      rateLabel: "מהירות השמעה",
      hearReply: "השמע תגובה",
      error: "כרגע לא הצלחתי לענות. נסה שוב.",
    },
    liveMode: {
      title: "קשה לי עכשיו",
      subtitle: "אני עוצר רגע ונושם. רק צעד קטן.",
      thoughtLabel: "מה עולה בי עכשיו?",
      thoughtPlaceholder: "אני כותב בקצרה…",
      bodyLabel: "איפה זה בגוף שלי?",
      bodyPlaceholder: "למשל: חזה, גרון, בטן",
      intensityLabel: "העוצמה שלי כרגע",
      intensitySoft: "עדין",
      intensityMid: "בינוני",
      intensityStrong: "חזק",
      cta: "אני רוצה מענה קצר",
      loading: "אני מקבל מענה קצר…",
      safety: "כרגע חשוב לי לפנות לעזרה מקצועית או לקו סיוע.",
      safetyCta: "אני פונה לעזרה עכשיו",
      back: "אני חוזר למסע",
      oneBreath: "נשימה אחת",
      oneSentence: "משפט אחד",
      oneAction: "פעולה קטנה",
      oneQuestion: "שאלה אחת",
    },
    paywall: {
      title: "כדי להמשיך במסע הזה",
      subtitle: "יום 1 פתוח בחינם. כדי להמשיך מעבר לכך יש תשלום חד‑פעמי לכל בעיה.",
      freeSubtitle: "יום 1 פתוח בחינם, והמשך המסע זמין לך כרגע ללא תשלום.",
      priceLabel: "מחיר",
      cta: "אני רוצה לפתוח את המסע",
      loading: "אני פותח…",
      back: "אני חוזר אחורה",
      error: "פתיחת המסע לא זמינה כרגע. נסה שוב בעוד רגע.",
    },
    about: {
      title: "אודות המסעות",
      body: [
        "המסעות כאן הם מרחב שבו אני יושב/ת עם עצמי בשקט, יום אחרי יום.",
        "בכל יום אני עוצר/ת לרגע, נושם/ת, וכותב/ת כמה שורות כמו שיחה פנימית.",
        "זה לא טיפול ולא תחליף לאיש מקצוע. אם יש סכנה לעצמי או לאחרים — אני פונה לעזרה מיידית.",
      ],
    },
    account: {
      title: "חשבון",
      logout: "התנתקות",
      continueToNext: "המשך למסע",
      plansTitle: "מסלולים",
      plansTrialLabel: "התנסות",
      oneTimeUnlockCardTitle: "פתיחה חד-פעמית למסע אחד",
      entitlementsTitle: "פתיחות שכבר רכשת",
      openJourney: "פתיחת מסע",
      noEntitlements: "עדיין לא נרכשו פתיחות בתשלום.",
      loadingPlans: "טוען מסלולים...",
      noPlans: "אין עדיין מסלולים זמינים.",
      startMonthly: "חודשי",
      oneTime: "חד-פעמי",
      errorLoadPlans: "טעינת מסלולים נכשלה",
      errorCheckout: "הקופה נכשלה",
      identitySectionTitle: "החשבון שלך",
      identitySignedInAs: "נכנסת עם האימייל:",
      identityLoggedInEmailLabel: "אימייל",
      displayNameLabel: "שם תצוגה:",
      identityNoEmailPasswordHint:
        "כדי לאפס או להגדיר סיסמה צריך אימייל בחשבון — אפשר לקשר אימייל למטה.",
      accountSecurityTitle: "אבטחה וסיסמה",
      accountPasswordHint:
        "נשלח קוד לאימייל שלך, ואז תבחר סיסמה חדשה (מינימום 8 תווים).",
      resetPasswordCta: "איפוס סיסמה",
      identityDeviceOnly:
        "אין לחשבון הזה כרגע אימייל משויך. נכנסת אוטומטית לפי המכשיר — הנתונים והמסע שמורים כאן במכשיר הזה.",
      identityInternalId: "מזהה פנימי (לתמיכה)",
      linkEmailSectionTitle: "חיבור אימייל לחשבון",
      linkEmailHint:
        "שדה זה לא להתחברות עם סיסמה. הוא מאפשר לשמור כתובת אימייל על אותו חשבון: אפשר לשחזר גישה ממכשיר אחר עם קוד שנשלח לאימייל, ולקבל תזכורות למייל אם הפעלת אותן.",
      linkEmailFieldLabel: "כתובת האימייל לקישור",
      linkEmailLabel: "אימייל לשחזור",
      linkEmailPlaceholder: "הקלידו כתובת אימייל",
      linkEmailCta: "שמור אימייל",
      linkEmailLoading: "שומר...",
      linkEmailSuccess: "האימייל קושר בהצלחה לחשבון.",
      genderSaving: "שומר…",
      genderSaved: "המגדר נשמר.",
      genderSaveFailed: "שמירת המגדר נכשלה.",
      subscriptionStatusTitle: "מנוי חודשי",
      subscriptionStatusActive: "פעיל",
      subscriptionStatusTrialing: "תקופת התנסות",
      subscriptionRenews: "חידוש/סיום תקופה",
      subscriptionCancelNotice: "המנוי יסתיים ללא חידוש בתום התקופה הנוכחית.",
      subscriptionPlanFallback: "מסלול",
      deviceAccountAuthHint:
        "רוצים חשבון עם אימייל וסיסמה כדי להיכנס מכל מכשיר? אפשר להירשם או להתחבר — והנתונים יישארו מקושרים אחרי שתקשרו את המכשיר לחשבון.",
      createFullAccount: "יצירת חשבון (אימייל)",
      alreadyHaveAccount: "יש לי כבר חשבון — התחברות",
      dataRightsHint: "מחיקת חשבון או קבלת עותק של הנתונים — פנו אלינו בדוא״ל:",
      remindersTitle: "תזכורות",
      remindersIntro: "אפשר לבחור איך ומתי לקבל תזכורות מהמסע שלך.",
      remindersEnabled: "קבל תזכורת יומית",
      remindersEmailEnabled: "שלח תזכורת למייל",
      remindersHour: "שעה",
      remindersMinute: "דקה",
      remindersTimezone: "אזור זמן",
      remindersLanguage: "שפת התזכורת",
      remindersSave: "שמור",
      remindersSendingTest: "שולח בדיקה...",
      remindersTest: "שלח תזכורת בדיקה",
      remindersSaved: "נשמר",
      remindersSaveFailed: "שמירה נכשלה",
      remindersTestSent: "תזכורת בדיקה נשלחה",
      remindersTestFailed: "שליחת בדיקה נכשלה",
      genderLabel: "מגדר",
      genderMale: "זכר",
      genderFemale: "נקבה",
      genderOther: "אחר",
      genderSave: "שמור מגדר",
      footerTagline: "נשמח לשמוע ממך",
      journeyHubTitle: "מה קורה עכשיו",
      activeJourneyTitle: "יש מסע שמחכה לך",
      activeJourneyBody:
        "אם תבחר להתחיל עכשיו, המערכת תחזיר אותך ישר ליום שמחכה לך.",
      newJourneyTitle: "עוד לא התחלת מסע פעיל",
      newJourneyBody:
        "אם זו הפעם הראשונה שלך, נתחיל במסך זיהוי קצר ומשם נוביל אותך למסע המתאים.",
      continueJourney: "להמשיך במסע",
      startJourney: "להתחיל מסע",
      chooseJourney: "לבחור מסע אחר",
    },
    disclaimer: {
      title: "לפני שמתחילים",
      body: [
        "InnerVoice הוא מרחב אישי של הקשבה פנימית.",
        "הוא לא טיפול רפואי או נפשי, ואינו תחליף לעזרה מקצועית.",
        "אם יש מחשבות על פגיעה בעצמך או באחרים, או תחושת סכנה, חשוב לפנות לעזרה מקצועית או לשירותי חירום.",
      ],
      checkboxLabel: "קראתי והבנתי",
      cta: "התחלת המסע",
    },
    problemSelection: {
      pageTitle: "מה הכי קרוב למה שעובר עליך עכשיו?\u200F",
      subtitle:
        "בחר את התחום שהכי קרוב למה שעובר עליך עכשיו. תמיד אפשר להתחיל מסע נוסף בהמשך.",
      checkoutSuccess: "התשלום הושלם. המסע פתוח עבורך.",
      checkoutCancel: "התשלום בוטל. אפשר לנסות שוב בכל רגע.",
      colLifeAreas: "תחומים מרכזיים",
      colLifeAreasEmpty: "אין כרגע נושאים זמינים בשפה זו.",
      colMoreSpecific: "נושאים ממוקדים",
      colMoreSpecificEmpty: "בחרו תחום חיים כדי לראות נושאים מוצעים.",
      colYourChoice: "הבחירה שלך",
      colYourChoiceEmpty: "בחרו נושא כדי להמשיך.",
      selectedProblemIntro: "בחרת להתחיל בנושא",
      selectedProblemHint: "תמיד אפשר לפתוח אחר כך מסע נוסף לנושאים אחרים שיעלו.",
      startButton: "להתחיל במסע הזה",
      searchPlaceholder: "חיפוש נושאים...",
      clearSearch: "נקה",
      allCategories: "כל הקטגוריות",
      mobileBackToAreas: "← חזרה לתחומי חיים",
      mobileBackToTopics: "← חזרה לנושאים המדויקים",
    },
    journeySummary: {
      title: "סיכום מסע",
      subtitle: "כל מה שכתבת כאן נשמר בשבילך",
      backToOverview: "חזרה למסע",
      openJournal: "פתיחת יומן מלא",
      intakeHeader: "איפה התחלתי",
      journalHeader: "היומן המרכזי שלי",
      finalSummaryHeader: "הסיכום האישי שלי",
      noFinalSummary: "עדיין אין סיכום אישי.",
      dayLabel: "יום",
      intakePain: "מה הכי כואב עכשיו",
      intakeGoal: "איך רציתי להרגיש בסוף",
      intakeWord: "מילה אחת על היום",
      journalEmpty: "אין עדיין רשומות ביומן למסע הזה.",
    },
  },
  en: {
    nav: {
      about: "About",
      companion: "Companion",
      journey: "Journey",
      problems: "Choose topic",
      account: "Account",
      home: "Home",
      voice: "Voice Chat",
    },
    progressBar: {
      title: "My journey progress",
      unit: "days completed",
    },
    journeyDay: {
      dayCount: (day, total) => `Day ${day} of ${total}`,
      backToJourney: "Back to journey",
      companion: "Companion",
      liveMode: "It's hard for me now",
      journeyPhaseStepTitle: "What is happening in me now",
      aiCoachCta: "Brief reflection",
      aiCoachLoading: "Preparing a brief reflection...",
      aiCoachTitle: "What is happening in me now",
      aiCoachStep: "My small step",
      aiCoachQuestion: "My one question",
      aiCoachRetry: "The reflection is not available right now",
      aiCoachError: "The reflection is not available right now.",
      aiCoachSafety: "Right now I choose to reach out to professional help or a local line.",
      aiCoachSafetyCta: "I reach out now",
      uploadAudio: "I add a recording",
      uploadingAudio: "I am uploading?",
      ttsCloud: "I listen with a cloud voice",
      play: "Play",
      pause: "Pause",
      listenToQuestionHint: "Listen to the question",
      writingQuestionHeading: "My question",
      writingZoneLabel: "My answer",
      postWritingMirrorTitle: "Here's what comes up from what you wrote",
      unifiedShortMirrorTitle: "This is what emerges from your words",
      mirrorJournalWithQuestion: (question, answer) =>
        `On this question — “${question}”: ${answer}`,
      continue: "Continue",
      back: "Back",
      skip: "Skip",
      record: "Answer by voice",
      stopRecord: "Stop",
      resetDay: "Start this day over",
      mindfulnessListeningSecondary: "I'll continue later",
      resetConfirm: "Reset this day and start over? All writing from this day will be deleted.",
      resetCancel: "Cancel",
      resetConfirmCta: "Reset and start over",
      questionLabel: (index) => `Question ${index}`,
      leadLine: "There is no right or wrong here. Only quiet truth.",
      missingContent: "Missing DNA POC content.",
      placeholderDefaults: [
        "Write gently? what comes up when you ask yourself this question?",
        "If there are no words ? that?s okay. Write just one word, or even ?I don?t know.?",
        "Notice your body for a moment. Where does it sit? Pressure, warmth, tightness? Write it simply.",
        "Sum it up in one sentence: what is the smallest truth you can say now?",
      ],
    },
    journeyOverview: {
      pageTitle: "Your healing journey ? overview",
      intakeSummaryTitle: "What I wrote at the start",
      daysSummaryTitle: "How you progressed across the days",
      journalCta: "Open my full journal",
      dayStatusNotStarted: "Not started yet",
      dayStatusInProgress: "In progress",
      dayStatusCompleted: "Completed",
      openDay: "Open this day",
      resumeDay: "Continue writing",
      completedDay: "View what you wrote",
      noIntakeYet: "You haven't filled the opening form for this journey yet.",
      goToIntake: "Go to intake form",
      dayLabel: "Day",
      dayLocked: "Opens after you finish the previous day",
      presenceLine: "If I want to pause for a moment, I am here.",
      insightsTitle: "A brief look at what repeats in me",
      insightsRepeating: "What repeats in me",
      insightsHelped: "What helped me",
      insightsNext: "My next step",
      moodTitle: "How do I feel today?",
      energyTitle: "How much energy do I have today?",
      saveSignals: "Save",
      continueLastDay: "Continue last day",
      chooseProblem: "Choose problem",
      summaryCta: "Journey summary",
      intakePainLabel: "What hurts most now:",
      intakeGoalLabel: "How I want to feel at the end:",
      intakeWordLabel: "One word for today:",
    },
    intake: {
      headerTitle: "Let's pause for a moment before we begin",
      introLine1: (topic) => `Before we start, I pause for a moment with this topic: ${topic}.`,
      introLine2: "I write honestly, one step at a time.",
      cardPainTitle: "In one sentence: what hurts most right now?",
      cardPainDesc: "Short. Simple. True.",
      cardPainPlaceholder: "One sentence here.",
      cardGoalTitle: "What would you want to feel differently by the end of the journey?",
      cardGoalDesc: "For example: calmer, less checking, more confidence, naming needs",
      cardGoalPlaceholder: "One sentence here.",
      cardWordTitle: "One word that describes how I feel right now",
      cardWordDesc: "Simple. Direct. One word.",
      cardWordPlaceholder: "One word",
      startJourney: "Continue to the journey",
      micStart: "Speak, I'll transcribe",
      micStop: "Stop voice",
      micNoSupport: "Browser doesn't support speech",
      micNoSupportApp:
        "Inside the InnerVoice app on iPhone/iPad, voice-to-text from the web view usually isn't available. Type in the fields, or open the same page in Safari or Chrome on your device.",
      micErrorBlocked:
        "Microphone access was blocked. On Android: Settings → InnerVoice → Permissions → Microphone. In a browser, allow recording when prompted.",
      micErrorNetwork: "Voice input failed (network or speech service). Try again or type instead.",
      micErrorGeneric: "Voice input stopped. Try again or keep typing.",
      chooseAnotherProblem: "Choose a different issue",
      requiredError: "To continue, complete the required fields.",
      summaryCta: "Start with the short version",
      summaryLoading: "Preparing the short version…",
      summaryTitle: "A short summary for me",
      summaryNeeds: "What matters to me now",
      summaryStart: "A starting line for me",
      summarySafety: "Right now I choose to reach out to professional help or a local line.",
      summarySafetyCta: "I reach out now",
    },
    helpNow: {
      title: "Help now",
      body: [
        "If you feel in immediate danger, or there are thoughts of harming yourself or others:",
        "Contact emergency services in your country right away (for example 911/112/999 by region).",
        "If possible, reach out to someone close and ask them to be with you now.",
        "If there is a local emotional support line, call it now.",
      ],
      back: "Back to journey",
      showLocal: "Show local emergency numbers",
      messageClose: "Message someone close",
    },
    journal: {
      title: "My full journal in this journey",
      dayHeaderPrefix: "Day",
      backToOverview: "Back to journey overview",
      noEntries: "No journal entries have been saved yet for this journey.",
      questionLabel: "Question",
      searchPlaceholder: "Search in your journal...",
      filterAll: "All days",
      exportJson: "Export JSON",
      exportTxt: "Export TXT",
      emptyDay: "No writing saved for this day yet.",
    },
    chat: {
      title: "Support from your InnerVoice",
      welcome: "Hi, I?m your InnerVoice. Share honestly what?s on your heart today and I?ll be with you step by step.",
      placeholder: "Write here what your heart wants to tell InnerVoice?",
      send: "Send",
      back: "? Back to today",
    },
    voiceChat: {
      title: "Your companion",
      subtitle: "Speak freely. I'm listening, and then I'll respond gently and truthfully.",
      introHint: "You can start with one sentence. That's enough for now.",
      back: "← Back to journey day",
      typePlaceholder: "Or write here instead of speaking...",
      micStart: "Start talking",
      micStop: "Stop recording",
      send: "Send",
      listening: "Listening...",
      processing: "Processing...",
      stop: "Stop playback",
      resume: "Resume playback",
      pause: "Play response",
      rateLabel: "Playback speed",
      hearReply: "Play response",
      error: "Something went wrong. Please try again.",
    },
    liveMode: {
      title: "It's hard for me now",
      subtitle: "I pause for a moment and breathe. Just one small step.",
      thoughtLabel: "What is rising in me now?",
      thoughtPlaceholder: "I write briefly?",
      bodyLabel: "Where is it in my body?",
      bodyPlaceholder: "For example: chest, throat, belly",
      intensityLabel: "My intensity right now",
      intensitySoft: "Soft",
      intensityMid: "Mid",
      intensityStrong: "Strong",
      cta: "I want a short response",
      loading: "I am getting a short response?",
      safety: "Right now I choose to reach out to professional help or a local line.",
      safetyCta: "I reach out now",
      back: "I return to my journey",
      oneBreath: "One breath",
      oneSentence: "One sentence",
      oneAction: "One small action",
      oneQuestion: "One question",
    },
    paywall: {
      title: "To continue this journey",
      subtitle: "Day 1 is free. To continue beyond that there is a one?time payment per problem.",
      freeSubtitle: "Day 1 is free, and continuing this journey is currently available at no cost.",
      priceLabel: "Price",
      cta: "I want to unlock this journey",
      loading: "Unlocking?",
      back: "Go back",
      error: "Something went wrong. Please try again soon.",
    },
    about: {
      title: "About the journeys",
      body: [
        "The journeys here are a space where I sit with myself quietly, day after day.",
        "Each day I pause for a moment, breathe, and write a few lines as an inner conversation.",
        "This is not treatment and not a replacement for a professional. If there is danger to myself or others ? I seek help immediately.",
      ],
    },
    account: {
      title: "Account",
      logout: "Logout",
      continueToNext: "Continue to journey",
      plansTitle: "Plans",
      entitlementsTitle: "Unlocked problems",
      openJourney: "Open journey",
      noEntitlements: "No paid problem unlocks yet.",
      loadingPlans: "Loading plans...",
      noPlans: "No active plans yet.",
      startMonthly: "Start monthly",
      oneTime: "One-time",
      errorLoadPlans: "Failed to load plans",
      errorCheckout: "Checkout failed",
      identitySectionTitle: "Your account",
      identitySignedInAs: "You are signed in with:",
      identityLoggedInEmailLabel: "Email",
      displayNameLabel: "Display name",
      identityNoEmailPasswordHint:
        "To reset or set a password, this account needs an email — you can link one below.",
      accountSecurityTitle: "Security & password",
      accountPasswordHint:
        "We will email you a code, then you can choose a new password (at least 8 characters).",
      resetPasswordCta: "Reset password",
      identityDeviceOnly:
        "This account does not have an email yet. You signed in automatically on this device — your journey and journal stay with this device session.",
      identityInternalId: "Internal ID (for support)",
      linkEmailSectionTitle: "Link an email to this account",
      linkEmailHint:
        "This field is not for password login. Adding an email lets you recover this same account on another device (with a code sent to email) and receive email reminders if you turn them on.",
      linkEmailFieldLabel: "Email address to link",
      linkEmailLabel: "Recovery email",
      linkEmailPlaceholder: "you@example.com",
      linkEmailCta: "Save email",
      linkEmailLoading: "Saving?",
      linkEmailSuccess: "Your email has been linked to this account.",
      genderSaving: "Saving…",
      genderSaved: "Gender saved.",
      genderSaveFailed: "Could not save gender.",
      genderLabel: "Gender",
      genderMale: "Male",
      genderFemale: "Female",
      genderOther: "Other",
      genderSave: "Save gender",
      subscriptionStatusTitle: "Monthly subscription",
      subscriptionStatusActive: "Active",
      subscriptionStatusTrialing: "Trial",
      subscriptionRenews: "Current period ends",
      subscriptionCancelNotice: "Your subscription will end after the current period (no renewal).",
      subscriptionPlanFallback: "Plan",
      deviceAccountAuthHint:
        "Want an email + password account so you can sign in on any device? Register or sign in — you can link this device session afterward.",
      createFullAccount: "Create account (email)",
      alreadyHaveAccount: "I already have an account — sign in",
      dataRightsHint: "Account deletion or a copy of your data — contact us at:",
      remindersTitle: "Reminders",
      remindersIntro: "Choose how and when to get reminders for your journey.",
      remindersEnabled: "Enable daily reminder",
      remindersEmailEnabled: "Send reminder by email",
      remindersHour: "Hour",
      remindersMinute: "Minute",
      remindersTimezone: "Timezone",
      remindersLanguage: "Reminder language",
      remindersSave: "Save",
      remindersSendingTest: "Sending test?",
      remindersTest: "Send test reminder",
      remindersSaved: "Saved",
      remindersSaveFailed: "Failed to save",
      remindersTestSent: "Test reminder sent",
      remindersTestFailed: "Test send failed",
      journeyHubTitle: "What happens next",
      activeJourneyTitle: "You have a journey waiting",
      activeJourneyBody:
        "If you choose to start now, the system will take you straight back to the day that is waiting for you.",
      newJourneyTitle: "You have not started an active journey yet",
      newJourneyBody:
        "If this is your first time, we will start with a short recognition screen and guide you to the right journey.",
      continueJourney: "Continue journey",
      startJourney: "Start journey",
      chooseJourney: "Choose another journey",
    },
    disclaimer: {
      title: "Before You Begin",
      body: [
        "InnerVoice is a personal self-guided experience of inner listening.",
        "It is not medical or psychological treatment and does not replace a professional.",
        "If there are thoughts of self-harm or harm to others, or a sense of danger ? I seek professional help or emergency services.",
      ],
      checkboxLabel: "I have read and understand",
      cta: "Start the journey",
    },
    problemSelection: {
      pageTitle: "What hurts the most right now?",
      subtitle: "Choose the area of life where the pain feels strongest. You can open another journey later.",
      checkoutSuccess: "Payment completed. The journey is now open for you.",
      checkoutCancel: "Payment was canceled. You can try again anytime.",
      colLifeAreas: "Core life areas",
      colLifeAreasEmpty: "No topics are available in this language yet.",
      colMoreSpecific: "More specific topics",
      colMoreSpecificEmpty: "Choose a life area to see suggested topics.",
      colYourChoice: "Your choice",
      colYourChoiceEmpty: "Choose a topic to continue.",
      selectedProblemIntro: "You chose to work on:",
      selectedProblemHint: "You can always start another journey later for other topics.",
      startButton: "Start healing journey for this topic",
      searchPlaceholder: "Search topics...",
      clearSearch: "Clear",
      allCategories: "All categories",
      mobileBackToAreas: "← Back to life areas",
      mobileBackToTopics: "← Back to topics",
    },
    journeySummary: {
      title: "Journey Summary",
      subtitle: "Everything you wrote here is saved for you",
      backToOverview: "Back to journey",
      openJournal: "Open full journal",
      intakeHeader: "Where you started",
      journalHeader: "Your central journal",
      finalSummaryHeader: "Your personal summary",
      noFinalSummary: "No final summary has been written yet.",
      dayLabel: "Day",
      intakePain: "What hurts most now",
      intakeGoal: "How you wanted to feel at the end",
      intakeWord: "One word for today",
      journalEmpty: "There are no journal entries yet for this journey.",
    },
  },
  es: {
    nav: {
      about: "Acerca",
      companion: "Acompañante",
      journey: "Viaje",
      problems: "Elegir tema",
      account: "Cuenta",
      home: "Inicio",
      voice: "Voz",
    },
    progressBar: {
      title: "Mi progreso del viaje",
      unit: "dias completados",
    },
    journeyDay: {
      dayCount: (day, total) => `Día ${day} de ${total}`,
      backToJourney: "Volver al viaje",
      companion: "Acompañante",
      liveMode: "Me cuesta ahora",
      journeyPhaseStepTitle: "Qué está pasando en mí ahora",
      aiCoachCta: "Reflejo breve",
      aiCoachLoading: "Preparando un reflejo breve...",
      aiCoachTitle: "Qué está pasando en mí ahora",
      aiCoachStep: "Mi paso pequeño",
      aiCoachQuestion: "Mi pregunta",
      aiCoachRetry: "El reflejo no está disponible ahora",
      aiCoachError: "El reflejo no está disponible ahora.",
      aiCoachSafety: "Ahora elijo buscar ayuda profesional o una línea local.",
      aiCoachSafetyCta: "Busco ayuda ahora",
      uploadAudio: "Añado una grabación",
      uploadingAudio: "Estoy subiendo?",
      ttsCloud: "Escucho con una voz en la nube",
      play: "Reproducir",
      pause: "Pausar",
      listenToQuestionHint: "Escuchar la pregunta",
      writingQuestionHeading: "Mi pregunta",
      writingZoneLabel: "Mi respuesta",
      postWritingMirrorTitle: "Cuando releo lo que escribí, esto es lo que noto en mí",
      unifiedShortMirrorTitle: "Esto es lo que surge de tus palabras",
      mirrorJournalWithQuestion: (question, answer) =>
        `Me quedé un momento con «${question}». Esto es lo que escribí para mí: ${answer}`,
      mirrorJournalNoQuestion: (answer) => `Otra línea que me salió al escribir: ${answer}`,
      continue: "Continuar",
      back: "Volver",
      skip: "Saltar",
      record: "Responder en voz",
      stopRecord: "Detener",
      resetDay: "Empezar el día de nuevo",
      mindfulnessListeningSecondary: "Continuaré más tarde",
      mindfulnessUpperMirrorLines: [
        "Noto que a veces, incluso cuando estoy con personas, no estoy del todo presente.",
        "Eso despierta en mí el deseo de entender qué me pasa en esos momentos.",
      ],
      resetConfirm: "¿Reiniciar este día y empezar de nuevo? Se borrará toda la escritura.",
      resetCancel: "Cancelar",
      resetConfirmCta: "Reiniciar",
      questionLabel: (index) => `Pregunta ${index}`,
      leadLine: "No hay correcto o incorrecto aquí. Solo verdad tranquila.",
      missingContent: "Falta contenido de DNA POC.",
      placeholderDefaults: [
        "Escribe con suavidad… ¿qué surge cuando te haces esta pregunta?",
        "Si no hay palabras — está bien. Escribe solo una palabra, o incluso “no sé”.",
        "Nota tu cuerpo un momento. ¿Dónde se siente? Presión, calor, tensión. Escríbelo simple.",
        "Resúmelo en una frase: ¿cuál es la verdad más pequeña que puedes decir ahora?",
      ],
    },
    journeyOverview: {
      pageTitle: "Tu viaje de sanación - vista general",
      intakeSummaryTitle: "Lo que escribí al empezar",
      daysSummaryTitle: "Cómo avanzaste a lo largo de los días",
      journalCta: "Abrir mi diario completo",
      dayStatusNotStarted: "Aún no empezado",
      dayStatusInProgress: "En proceso",
      dayStatusCompleted: "Completado",
      openDay: "Abrir este día",
      resumeDay: "Continuar escribiendo",
      completedDay: "Ver lo que escribiste",
      noIntakeYet: "Todavía no llenaste el formulario inicial para este viaje.",
      goToIntake: "Ir al formulario inicial",
      dayLabel: "Día",
      dayLocked: "Se abrirá tras completar el día anterior",
      presenceLine: "Si quiero detenerme un momento, estoy aqui.",
      insightsTitle: "Una mirada breve a lo que se repite en mi",
      insightsRepeating: "Lo que se repite en mi",
      insightsHelped: "Lo que me ayudo",
      insightsNext: "Mi siguiente paso",
      moodTitle: "Como me siento hoy?",
      energyTitle: "Cuanta energia tengo hoy?",
      saveSignals: "Guardar",
      continueLastDay: "Volver al ultimo dia",
      chooseProblem: "Elegir problema",
      summaryCta: "Resumen del viaje",
      intakePainLabel: "Lo más difícil ahora:",
      intakeGoalLabel: "Cómo quiero sentirme al final:",
      intakeWordLabel: "Una palabra para hoy:",
    },
    intake: {
      headerTitle: "Paremos un momento antes de comenzar",
      introLine1: (topic) => `Antes de empezar, me detengo un momento con este tema: ${topic}.`,
      introLine2: "Escribo con honestidad, un paso cada vez.",
      cardPainTitle: "En una frase: ¿qué es lo más difícil ahora mismo?",
      cardPainDesc: "Corto. Simple. Verdadero.",
      cardPainPlaceholder: "Una frase aquí.",
      cardGoalTitle: "¿Qué te gustaría que se sintiera diferente al final del viaje?",
      cardGoalDesc: "Por ejemplo: más calma, menos revisar, más confianza, nombrar necesidades",
      cardGoalPlaceholder: "Una frase aquí.",
      cardWordTitle: "Una palabra que describa cómo me siento ahora",
      cardWordDesc: "Simple. Directa. Una palabra.",
      cardWordPlaceholder: "Una palabra",
      startJourney: "Continuar al viaje",
      micStart: "Habla, lo transcribiré",
      micStop: "Detener voz",
      micNoSupport: "El navegador no soporta voz",
      micNoSupportApp:
        "En la app InnerVoice en iPhone/iPad, la voz a texto del navegador integrado suele no estar disponible. Escribe en los campos, o abre la misma pagina en Safari o Chrome.",
      micErrorBlocked:
        "Microfono bloqueado. En Android: Ajustes → InnerVoice → Permisos → Microfono. En el navegador, permite el microfono si pregunta.",
      micErrorNetwork: "Fallo de voz (red o servicio). Reintenta o escribe a mano.",
      micErrorGeneric: "La voz se detuvo. Reintenta o sigue escribiendo.",
      chooseAnotherProblem: "Elegir otro tema",
      requiredError: "Para continuar, completa los campos obligatorios.",
      summaryCta: "Empezar con la versión breve",
      summaryLoading: "Preparando la versión breve…",
      summaryTitle: "Un resumen breve para mi",
      summaryNeeds: "Lo que me importa ahora",
      summaryStart: "Una frase de inicio para mi",
      summarySafety: "Ahora elijo buscar ayuda profesional o una linea local.",
      summarySafetyCta: "Busco ayuda ahora",
    },
    helpNow: {
      title: "Ayuda ahora",
      body: [
        "Si te sientes en peligro inmediato, o hay pensamientos de hacerte daño a ti u a otros:",
        "Contacta de inmediato a los servicios de emergencia de tu país (por ejemplo 911/112/999 según la región).",
        "Si es posible, contacta a alguien cercano y pide que esté contigo ahora.",
        "Si hay una línea local de apoyo emocional, llámala ahora.",
      ],
      back: "Volver al viaje",
      showLocal: "Mostrar números de emergencia locales",
      messageClose: "Enviar un mensaje a alguien cercano",
    },
    journal: {
      title: "Mi diario completo en este viaje",
      dayHeaderPrefix: "Dia",
      backToOverview: "Volver al resumen del viaje",
      noEntries: "Aun no se han guardado entradas de diario en este viaje.",
      questionLabel: "Pregunta",
      searchPlaceholder: "Buscar en tu diario...",
      filterAll: "Todos los días",
      exportJson: "Exportar JSON",
      exportTxt: "Exportar TXT",
      emptyDay: "Aún no hay escritura guardada para este día.",
    },
    chat: {
      title: "Acompañamiento de tu InnerVoice",
      welcome: "Hola, soy tu InnerVoice. Cuéntame con sinceridad qué ocupa tu corazón hoy y te acompañaré paso a paso.",
      placeholder: "Escribe aquí lo que tu corazón quiere decirle a InnerVoice…",
      send: "Enviar",
      back: "← Volver al día",
    },
    voiceChat: {
      title: "Tu acompañante",
      subtitle: "Habla con libertad. Te escucho, y luego responderé con suavidad y verdad.",
      introHint: "Puedes empezar con una frase. Por ahora no hace falta más.",
      back: "← Volver al día del viaje",
      typePlaceholder: "O escribe aquí en lugar de hablar...",
      micStart: "Empezar a hablar",
      micStop: "Detener grabación",
      send: "Enviar",
      listening: "Escuchando...",
      processing: "Procesando...",
      stop: "Detener reproducción",
      resume: "Reanudar reproducción",
      pause: "Reproducir respuesta",
      rateLabel: "Velocidad de reproducción",
      hearReply: "Reproducir respuesta",
      error: "Algo salió mal. Intenta de nuevo.",
    },
    liveMode: {
      title: "Me cuesta ahora",
      subtitle: "Me detengo un momento y respiro. Solo un paso pequeno.",
      thoughtLabel: "¿Que aparece en mi ahora?",
      thoughtPlaceholder: "Escribo breve?",
      bodyLabel: "¿Donde esta en mi cuerpo?",
      bodyPlaceholder: "Por ejemplo: pecho, garganta, vientre",
      intensityLabel: "Mi intensidad ahora",
      intensitySoft: "Suave",
      intensityMid: "Media",
      intensityStrong: "Fuerte",
      cta: "Quiero una respuesta breve",
      loading: "Estoy recibiendo una respuesta breve?",
      safety: "Ahora elijo buscar ayuda profesional o una linea local.",
      safetyCta: "Busco ayuda ahora",
      back: "Vuelvo a mi viaje",
      oneBreath: "Una respiracion",
      oneSentence: "Una frase",
      oneAction: "Una accion pequena",
      oneQuestion: "Una pregunta",
    },
    paywall: {
      title: "Para continuar este viaje",
      subtitle: "El día 1 es gratis. Para continuar hay un pago único por cada tema.",
      freeSubtitle: "El día 1 es gratis, y continuar este viaje esta disponible sin costo por ahora.",
      priceLabel: "Precio",
      cta: "Quiero desbloquear este viaje",
      loading: "Desbloqueando?",
      back: "Volver",
      error: "Algo salió mal. Intenta de nuevo en un momento.",
    },
    about: {
      title: "Acerca de los viajes",
      body: [
        "Los viajes aquí son un espacio donde me siento conmigo en silencio, día tras día.",
        "Cada día hago una pausa un momento, respiro y escribo unas pocas líneas como una conversación interior.",
        "Esto no es tratamiento ni reemplaza a un profesional. Si hay peligro para mí o para otros — busco ayuda de inmediato.",
      ],
    },
    account: {
      title: "Cuenta",
      logout: "Cerrar sesión",
      continueToNext: "Continuar al viaje",
      plansTitle: "Planes",
      entitlementsTitle: "Problemas desbloqueados",
      openJourney: "Abrir viaje",
      noEntitlements: "Aún no hay desbloqueos de problemas pagados.",
      loadingPlans: "Cargando planes...",
      noPlans: "Aún no hay planes activos.",
      startMonthly: "Mensual",
      oneTime: "Pago único",
      errorLoadPlans: "No se pudieron cargar los planes",
      errorCheckout: "Fallo en el checkout",
      identitySectionTitle: "Tu cuenta",
      identitySignedInAs: "Entraste con el correo:",
      identityLoggedInEmailLabel: "Correo",
      displayNameLabel: "Nombre para mostrar",
      identityNoEmailPasswordHint:
        "Para restablecer o crear una contraseña hace falta un correo en la cuenta — puedes vincular uno abajo.",
      accountSecurityTitle: "Seguridad y contraseña",
      accountPasswordHint:
        "Te enviaremos un codigo por correo y luego podras elegir una contraseña nueva (minimo 8 caracteres).",
      resetPasswordCta: "Restablecer contraseña",
      identityDeviceOnly:
        "Esta cuenta aún no tiene correo. Entraste automáticamente en este dispositivo: el viaje y el diario quedan ligados a esta sesión del dispositivo.",
      identityInternalId: "ID interno (soporte)",
      linkEmailSectionTitle: "Vincular un correo a esta cuenta",
      linkEmailHint:
        "Este campo no es para la contraseña. Al añadir un correo puedes recuperar esta misma cuenta en otro dispositivo (con un código por correo) y recibir recordatorios por correo si los activas.",
      linkEmailFieldLabel: "Correo a vincular",
      linkEmailLabel: "Correo de recuperación",
      linkEmailPlaceholder: "tu@ejemplo.com",
      linkEmailCta: "Guardar correo",
      linkEmailLoading: "Guardando?",
      linkEmailSuccess: "Tu correo fue vinculado a esta cuenta.",
      genderSaving: "Guardando…",
      genderSaved: "Genero guardado.",
      genderSaveFailed: "No se pudo guardar el genero.",
      genderLabel: "Genero",
      genderMale: "Masculino",
      genderFemale: "Femenino",
      genderOther: "Otro",
      genderSave: "Guardar genero",
      subscriptionStatusTitle: "Suscripcion mensual",
      subscriptionStatusActive: "Activa",
      subscriptionStatusTrialing: "Periodo de prueba",
      subscriptionRenews: "Fin del periodo actual",
      subscriptionCancelNotice:
        "La suscripcion terminara sin renovacion al final del periodo actual.",
      subscriptionPlanFallback: "Plan",
      deviceAccountAuthHint:
        "Quieres una cuenta con correo y contrasena para entrar desde cualquier dispositivo? Registrate o entra — luego puedes vincular esta sesion.",
      createFullAccount: "Crear cuenta (correo)",
      alreadyHaveAccount: "Ya tengo cuenta — entrar",
      dataRightsHint: "Borrar la cuenta o recibir una copia de tus datos — escribenos a:",
      remindersTitle: "Recordatorios",
      remindersIntro: "Puedes elegir como y cuando recibir recordatorios de tu viaje.",
      remindersEnabled: "Activar recordatorio diario",
      remindersEmailEnabled: "Enviar recordatorio por correo",
      remindersHour: "Hora",
      remindersMinute: "Minuto",
      remindersTimezone: "Zona horaria",
      remindersLanguage: "Idioma del recordatorio",
      remindersSave: "Guardar",
      remindersSendingTest: "Enviando prueba?",
      remindersTest: "Enviar recordatorio de prueba",
      remindersSaved: "Guardado",
      remindersSaveFailed: "No se pudo guardar",
      remindersTestSent: "Recordatorio de prueba enviado",
      remindersTestFailed: "Fallo al enviar prueba",
      journeyHubTitle: "Que sigue ahora",
      activeJourneyTitle: "Tienes un viaje esperandote",
      activeJourneyBody:
        "Si eliges empezar ahora, el sistema te lleva directo al dia que te espera.",
      newJourneyTitle: "Aun no empezaste un viaje activo",
      newJourneyBody:
        "Si es tu primera vez, empezamos con una pantalla breve de reconocimiento y te guiamos al viaje adecuado.",
      continueJourney: "Continuar el viaje",
      startJourney: "Empezar viaje",
      chooseJourney: "Elegir otro viaje",
    },
    disclaimer: {
      title: "Antes de comenzar",
      body: [
        "InnerVoice es un espacio personal de escucha interna.",
        "No es tratamiento médico ni psicológico, y no reemplaza a un profesional.",
        "Si tienes pensamientos de hacerte daño o dañar a otras personas, o una sensación de peligro, es importante buscar ayuda profesional o servicios de emergencia.",
      ],
      checkboxLabel: "He leído y entiendo",
      cta: "Comenzar el viaje",
    },
    problemSelection: {
      pageTitle: "Que es lo que mas duele ahora?",
      subtitle: "Elige el area de tu vida donde el dolor se siente mas fuerte. Siempre podras abrir otro viaje luego.",
      checkoutSuccess: "Pago completado. El viaje ya esta disponible para ti.",
      checkoutCancel: "El pago fue cancelado. Puedes intentarlo de nuevo cuando quieras.",
      colLifeAreas: "Areas principales de la vida",
      colLifeAreasEmpty: "No hay temas disponibles en este idioma.",
      colMoreSpecific: "Temas mas especificos",
      colMoreSpecificEmpty: "Elige un area para ver los temas sugeridos.",
      colYourChoice: "Tu eleccion",
      colYourChoiceEmpty: "Elige un tema para continuar.",
      selectedProblemIntro: "Elegiste trabajar el tema:",
      selectedProblemHint: "Siempre podras abrir otro viaje mas adelante.",
      startButton: "Comenzar un viaje de sanacion con este tema",
      searchPlaceholder: "Buscar temas...",
      clearSearch: "Limpiar",
      allCategories: "Todas las categorias",
      mobileBackToAreas: "← Volver a las áreas",
      mobileBackToTopics: "← Volver a los temas",
    },
    journeySummary: {
      title: "Resumen del viaje",
      subtitle: "Todo lo que escribiste aquí queda guardado",
      backToOverview: "Volver al viaje",
      openJournal: "Abrir diario completo",
      intakeHeader: "Dónde empezaste",
      journalHeader: "Tu diario central",
      finalSummaryHeader: "Tu resumen personal",
      noFinalSummary: "Aún no hay un resumen personal escrito.",
      dayLabel: "Día",
      intakePain: "Lo más difícil ahora",
      intakeGoal: "Cómo querías sentirte al final",
      intakeWord: "Una palabra para hoy",
      journalEmpty: "Aún no hay entradas en el diario para este viaje.",
    },
  },
};

function applySectionOverrides(strings, overrides) {
  const next = { ...strings };
  Object.entries(overrides || {}).forEach(([key, value]) => {
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      next[key] &&
      typeof next[key] === "object" &&
      !Array.isArray(next[key])
    ) {
      next[key] = { ...next[key], ...value };
      return;
    }
    next[key] = value;
  });
  return next;
}

function getHebrewGenderOverrides(gender = "other") {
  const safeGender = String(gender || "other").toLowerCase();
  return {
    journeyDay: {
      placeholderDefaults: [
        pickGenderText(
          {
            male: "כתוב כאן בעדינות… מה עולה לך כשאתה שואל את עצמך את השאלה הזו?",
            female: "כתבי כאן בעדינות… מה עולה לך כשאת שואלת את עצמך את השאלה הזו?",
            other: "כתוב/כתבי כאן בעדינות… מה עולה לך כשאת/ה שואל/ת את עצמך את השאלה הזו?",
          },
          safeGender
        ),
        "",
        "שים לב רגע לגוף. איפה זה יושב? לחץ, חום, כיווץ? כתוב את זה פשוט.",
        pickGenderText(
          {
            male: "סכם במשפט אחד: מה האמת הכי קטנה שאתה יכול להגיד עכשיו?",
            female: "סכמי במשפט אחד: מה האמת הכי קטנה שאת יכולה להגיד עכשיו?",
            other: "סכם/י במשפט אחד: מה האמת הכי קטנה שאת/ה יכול/ה להגיד עכשיו?",
          },
          safeGender
        ),
      ],
    },
    journeyOverview: {
      pageTitle: "מסע הריפוי שלך – מבט מלמעלה",
      moodTitle: pickGenderText(
        {
          male: "איך אני מרגיש היום?",
          female: "איך אני מרגישה היום?",
          other: "איך אני מרגיש/ה היום?",
        },
        safeGender
      ),
    },
    intake: {
      introLine1: (topic) =>
        `${pickGenderText(
          {
            male: "לפני שמתחילים, אני עוצר רגע מול הנושא",
            female: "לפני שמתחילים, אני עוצרת רגע מול הנושא",
            other: "לפני שמתחילים, אני עוצר/ת רגע מול הנושא",
          },
          safeGender
        )}: ${topic}.`,
      introLine2: pickGenderText(
        {
          male: "אני כותב בכנות, צעד אחד בכל פעם.",
          female: "אני כותבת בכנות, צעד אחד בכל פעם.",
          other: "אני כותב/ת בכנות, צעד אחד בכל פעם.",
        },
        safeGender
      ),
      cardWordTitle: pickGenderText(
        {
          male: "מילה אחת שמתארת איך אני מרגיש עכשיו",
          female: "מילה אחת שמתארת איך אני מרגישה עכשיו",
          other: "מילה אחת שמתארת איך אני מרגיש/ה עכשיו",
        },
        safeGender
      ),
      micStart: pickGenderText(
        {
          male: "דבר, אתמלל",
          female: "דברי, אתמלל",
          other: "דבר/י, אתמלל",
        },
        safeGender
      ),
    },
    helpNow: {
      body: [
        pickGenderText(
          {
            male: "אם אתה מרגיש בסכנה מיידית, או שיש מחשבות לפגוע בעצמך או באחרים:",
            female: "אם את מרגישה בסכנה מיידית, או שיש מחשבות לפגוע בעצמך או באחרים:",
            other: "אם את/ה מרגיש/ה בסכנה מיידית, או שיש מחשבות לפגוע בעצמך או באחרים:",
          },
          safeGender
        ),
        `${pickGenderText(
          {
            male: "פנה מיד לשירותי חירום במדינה שלך",
            female: "פני מיד לשירותי חירום במדינה שלך",
            other: "פנה/י מיד לשירותי חירום במדינה שלך",
          },
          safeGender
        )} (למשל 911/112/999 לפי אזור).`,
        pickGenderText(
          {
            male: "אם אפשר, פנה לאדם קרוב ובקש להיות איתך עכשיו.",
            female: "אם אפשר, פני לאדם קרוב ובקשי להיות איתך עכשיו.",
            other: "אם אפשר, פנה/י לאדם קרוב ובקש/י להיות איתך עכשיו.",
          },
          safeGender
        ),
        "אם יש מוקד תמיכה רגשי מקומי, התקשר/י אליו עכשיו.",
      ],
    },
    chat: {
      title: "ליווי עם InnerVoice",
      welcome: pickGenderText(
        {
          male: "היי, אני InnerVoice. תכתוב לי בכנות מה מעסיק אותך היום, ואני אהיה איתך צעד־צעד.",
          female: "היי, אני InnerVoice. תכתבי לי בכנות מה מעסיק אותך היום, ואני אהיה איתך צעד־צעד.",
          other: "היי, אני InnerVoice. תכתוב/תכתבי לי בכנות מה מעסיק אותך היום, ואני אהיה איתך צעד־צעד.",
        },
        safeGender
      ),
      placeholder: pickGenderText(
        {
          male: "תכתוב כאן מה שהלב שלך רוצה לספר ל־InnerVoice…",
          female: "תכתבי כאן מה שהלב שלך רוצה לספר ל־InnerVoice…",
          other: "תכתוב/תכתבי כאן מה שהלב שלך רוצה לספר ל־InnerVoice…",
        },
        safeGender
      ),
    },
    voiceChat: {
      title: "המלווה שלך",
      micStart: pickGenderText(
        {
          male: "התחל לדבר",
          female: "התחילי לדבר",
          other: "התחל/י לדבר",
        },
        safeGender
      ),
    },
    liveMode: {
      thoughtPlaceholder: pickGenderText(
        {
          male: "אני כותב בקצרה…",
          female: "אני כותבת בקצרה…",
          other: "אני כותב/ת בקצרה…",
        },
        safeGender
      ),
      back: pickGenderText(
        {
          male: "אני חוזר למסע",
          female: "אני חוזרת למסע",
          other: "אני חוזר/ת למסע",
        },
        safeGender
      ),
    },
    about: {
      body: [
        pickGenderText(
          {
            male: "המסעות כאן הם מרחב שבו אני יושב עם עצמי בשקט, יום אחרי יום.",
            female: "המסעות כאן הם מרחב שבו אני יושבת עם עצמי בשקט, יום אחרי יום.",
            other: "המסעות כאן הם מרחב שבו אני יושב/ת עם עצמי בשקט, יום אחרי יום.",
          },
          safeGender
        ),
        pickGenderText(
          {
            male: "בכל יום אני עוצר לרגע, נושם, וכותב כמה שורות כמו שיחה פנימית.",
            female: "בכל יום אני עוצרת לרגע, נושמת, וכותבת כמה שורות כמו שיחה פנימית.",
            other: "בכל יום אני עוצר/ת לרגע, נושם/ת, וכותב/ת כמה שורות כמו שיחה פנימית.",
          },
          safeGender
        ),
        "זה לא טיפול ולא תחליף לאיש מקצוע. אם יש סכנה לעצמי או לאחרים, אני פונה לעזרה מיידית.",
      ],
    },
    problemSelection: {
      pageTitle: "מה הכי קרוב למה שעובר עליך עכשיו?\u200F",
      subtitle: pickGenderText(
        {
          male:
            "בחר את התחום שהכי קרוב למה שעובר עליך עכשיו. תמיד אפשר להתחיל מסע נוסף בהמשך.",
          female:
            "בחרי את התחום שהכי קרוב למה שעובר עליך עכשיו. תמיד אפשר להתחיל מסע נוסף בהמשך.",
          other:
            "בחר את התחום שהכי קרוב למה שעובר עליך עכשיו. תמיד אפשר להתחיל מסע נוסף בהמשך.",
        },
        safeGender
      ),
      colYourChoice: "הבחירה שלך",
    },
  };
}

export function getSystemStrings(lang, gender = "other") {
  const safe = normalizeLang(lang || "he");
  const base = SYSTEM_STRINGS[safe] || SYSTEM_STRINGS.he;
  if (safe !== "he") return base;
  return applySectionOverrides(base, getHebrewGenderOverrides(gender));
}
